import React, { useEffect, useRef, useState } from 'react';
import { useToastMessage } from 'hooks';
import NoPayRestrictionControl from './NoPayRestrictionControl';
import {
	getNoPayRestriction,
	updateNoPayRestriction,
} from './noPayRestrictionApi';

export const getNoPayRestrictionErrorMessage = error => {
	const value = Array.isArray(error) ? error[0] : error || {};
	if (value.httpStatus === 403)
		return 'No tiene autorización para gestionar la restricción no-pay.';
	if (value.httpStatus === 404)
		return 'La orden no está disponible dentro de su alcance.';
	if (value.httpStatus === 409)
		return (
			value.message || 'La orden cambió y la excepción ya no se puede aplicar.'
		);
	if (value.httpStatus === 400)
		return value.message || 'Revise la razón ingresada.';
	return value.message || 'No se pudo gestionar la restricción no-pay.';
};

export default function NoPayRestriction({ orderId, onChanged }) {
	const [restriction, setRestriction] = useState();
	const [loadError, setLoadError] = useState();
	const sendToast = useToastMessage();
	const mountedRef = useRef(true);
	const orderIdRef = useRef(orderId);
	const requestGenerationRef = useRef(0);
	orderIdRef.current = orderId;

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			requestGenerationRef.current += 1;
		};
	}, []);

	useEffect(() => {
		const generation = requestGenerationRef.current + 1;
		requestGenerationRef.current = generation;
		setRestriction(undefined);
		setLoadError(undefined);
		if (!orderId) return undefined;

		getNoPayRestriction(orderId)
			.then(response => {
				if (
					mountedRef.current &&
					orderIdRef.current === orderId &&
					requestGenerationRef.current === generation
				) {
					setRestriction(response);
					setLoadError(undefined);
				}
			})
			.catch(error => {
				if (
					!mountedRef.current ||
					orderIdRef.current !== orderId ||
					requestGenerationRef.current !== generation
				)
					return;
				const message = getNoPayRestrictionErrorMessage(error);
				setLoadError(message);
				sendToast({
					message,
					type: 'error',
				});
			});

		return () => {
			if (requestGenerationRef.current === generation)
				requestGenerationRef.current += 1;
		};
	}, [orderId]);

	const handleSubmit = async payload => {
		const submittedOrderId = orderId;
		const submissionGeneration = requestGenerationRef.current;
		await updateNoPayRestriction(submittedOrderId, payload);
		if (
			!mountedRef.current ||
			orderIdRef.current !== submittedOrderId ||
			requestGenerationRef.current !== submissionGeneration
		)
			return undefined;

		const authoritativeGeneration = requestGenerationRef.current + 1;
		requestGenerationRef.current = authoritativeGeneration;
		const authoritative = await getNoPayRestriction(submittedOrderId);
		if (
			!mountedRef.current ||
			orderIdRef.current !== submittedOrderId ||
			requestGenerationRef.current !== authoritativeGeneration
		)
			return undefined;

		setRestriction(authoritative);
		sendToast({
			message: 'La restricción no-pay se actualizó correctamente.',
			type: 'success',
		});
		if (onChanged) onChanged(authoritative);
		return authoritative;
	};

	const handleError = error => {
		if (!mountedRef.current || orderIdRef.current !== orderId) return;
		sendToast({
			message: getNoPayRestrictionErrorMessage(error),
			type: 'error',
		});
	};

	return (
		<NoPayRestrictionControl
			key={orderId}
			restriction={restriction}
			loadError={loadError}
			onSubmit={handleSubmit}
			onError={handleError}
		/>
	);
}
